import app from '../app';
import FormModal from './FormModal';
import Stream from '../utils/Stream';
import classList from '../utils/classList';
import Avatar from './Avatar';
import Button from './Button';
import LoadingIndicator from './LoadingIndicator';
import { throttle } from '../utils/throttleDebounce';
import InfoTile from './InfoTile';
import UserSearchResult from '../../common/components/UserSearchResult';
import Pill from './Pill';
/**
 * The `UserSelectionModal` component displays a modal dialog with searchable
 * user list and submit button. The user can select one or more users from the
 * list and submit them to the callback.
 */
export default class UserSelectionModal extends FormModal {
  search = (() => Stream(''))();
  focused = false;
  results = (() => Stream({}))();
  oninit(vnode) {
    super.oninit(vnode);
    this.selected = Stream(this.attrs.selected || []);

    // preload.
    this.load();
  }
  className() {
    return 'UserSelectionModal Modal--simple';
  }
  title() {
    return this.attrs.title || app.translator.trans('core.lib.user_selection_modal.title');
  }
  content() {
    let list = this.attrs.maxItems && this.selected().length === this.attrs.maxItems ? this.selected() : this.results()[this.search()] || [];
    const excluded = this.attrs.excluded;
    if (typeof excluded === 'function') {
      list = list.filter(user => !excluded(user));
    } else if (excluded) {
      list = list.filter(user => !excluded.map(String).includes(user.id()));
    }
    return [m("div", {
      className: "Modal-body"
    }, m("div", {
      className: "UserSelectionModal-form"
    }, m("div", {
      className: "UserSelectionModal-form-input"
    }, m("input", {
      type: "text",
      className: "FormControl",
      placeholder: this.attrs.maxItems ? app.translator.trans('core.lib.user_selection_modal.max_items_to_select', {
        count: this.attrs.maxItems
      }, true) : app.translator.trans('core.lib.user_selection_modal.search_placeholder'),
      value: this.search(),
      oninput: e => {
        this.search(e.target.value);
        this.load();
      },
      onfocus: () => this.focused = true,
      onblur: () => this.focused = false,
      disabled: this.attrs.maxItems && this.selected().length === this.attrs.maxItems
    })), m("div", {
      className: "UserSelectionModal-form-submit App-primaryControl"
    }, m(Button, {
      type: "submit",
      className: "Button Button--primary",
      disabled: !this.meetsRequirements(),
      icon: "fas fa-check"
    }, app.translator.trans('core.lib.user_selection_modal.submit_button')))), m("div", {
      className: "UserSelectionModal-selected Pill-list"
    }, this.selected().map(user => m(Pill, {
      deletable: true,
      ondelete: () => {
        const selected = this.selected().filter(u => u !== user);
        this.selected(selected);
        if (!selected.length) {
          this.load();
        }
      },
      className: "Pill-alt"
    }, m(Avatar, {
      user: user
    }), user.displayName())))), this.loading || this.results()[this.search()] ? m("div", {
      className: "Modal-footer"
    }, this.loading ? m(LoadingIndicator, null) : this.results() && !this.results()[this.search()]?.length ? m(InfoTile, {
      icon: "fas fa-search"
    }, app.translator.trans('core.lib.user_selection_modal.empty_results')) : m("ul", {
      className: "UserSelectionModal-list Dropdown-menu"
    }, list.map(user => this.userListItem(user)))) : null];
  }
  userListItem(user) {
    const selected = this.selected().includes(user);
    const unavailable = this.attrs.unavailable?.(user);
    return m(UserSearchResult, {
      user: user,
      query: this.search(),
      className: classList({
        'UserSelectionModal-listItem': true,
        'UserSelectionModal-listItem--selected': selected,
        'UserSelectionModal-listItem--unavailable': !!unavailable
      }),
      disabled: !!unavailable,
      onclick: () => {
        if (selected) {
          this.selected(this.selected().filter(u => u !== user));
        } else {
          this.selected([...this.selected(), user]);
        }
      }
    }, unavailable ? m("span", {
      className: "UserSelectionModal-listItem-reason"
    }, unavailable) : m("input", {
      type: "checkbox",
      checked: selected,
      readOnly: true
    }));
  }
  meetsRequirements() {
    return this.selected().length > 0 && this.selected().length <= (this.attrs.maxItems || Infinity) && !this.selected().some(user => this.attrs.unavailable?.(user));
  }
  onsubmit(e) {
    e.preventDefault();
    if (this.attrs.onsubmit) this.attrs.onsubmit(this.selected());
    this.hide();
  }
  load = (() => throttle(500, () => {
    if (this.results()[this.search()]) return;
    if (this.attrs.maxItems && this.selected().length === this.attrs.maxItems) return;
    this.loading = true;
    return app.store.find('users', {
      filter: {
        q: this.search()
      }
    }).then(results => {
      this.results({
        ...this.results(),
        [this.search()]: results
      });
    }).finally(() => {
      this.loading = false;
      m.redraw();
    });
  }))();
}
flarum.reg.add('core', 'common/components/UserSelectionModal', UserSelectionModal);